/**
 * IncidentDetailsView - View for displaying incident details
 * Epic 6: Quality/Incidents/Exceptions
 */

import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack,
  Upload,
  Person,
  Schedule,
  Warning,
  Description,
  Timeline,
  AddCircle,
  AttachFile,
  ChangeCircle,
  Assignment,
  CheckCircle,
  Close,
  ArrowUpward,
  ArrowDownward,
  Sort,
  FilterList,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useIncident } from "../hooks/useIncidents";
import {
  updateIncidentStatus,
  addIncidentEvidence,
  attributeResponsibility,
} from "../services/incidentService";
import MediaItemsGallery from "../../../components/media/MediaItemsGallery.jsx";

const STATUS_OPTIONS = [
  "OPEN",
  "UNDER_REVIEW",
  "AWAITING_EVIDENCE",
  "RESOLVED",
  "CLOSED",
];

const RESPONSIBILITY_OPTIONS = [
  "PROVIDER",
  "CUSTOMER",
  "OPS",
  "SHARED",
  "UNDETERMINED",
];

/**
 * Helper function to detect file type from mimeType
 */
const getFileTypeFromMime = (mimeType) => {
  if (!mimeType) return "document";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType === "application/pdf") return "document";
  return "document";
};

/**
 * IncidentDetailsView Component
 */
const IncidentDetailsView = () => {
  const { orderId, incidentId } = useParams();
  const navigate = useNavigate();

  const { incident, loading, error, fetchIncident } = useIncident(orderId, incidentId);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [responsibilityDialogOpen, setResponsibilityDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [activityOrder, setActivityOrder] = useState("desc"); // "desc" = newest first, "asc" = oldest first
  const [activityFilter, setActivityFilter] = useState("all"); // "all" | "evidence" | "status" | "responsibility" | "system"
  const [expandedBundles, setExpandedBundles] = useState(new Set()); // Track which bundles are expanded
  const [previewItem, setPreviewItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Map evidence items to MediaItemsGallery format
  const evidenceItems = useMemo(() => {
    if (!incident?.evidence || incident.evidence.length === 0) return [];
    return incident.evidence.map((ev) => ({
      id: ev.id,
      type: getFileTypeFromMime(ev.mimeType),
      filename: ev.filename,
      description: ev.description || "", // Show description if available, empty string if not
      downloadUrl: ev.downloadUrl,
      previewUrl: ev.downloadUrl, // Use downloadUrl as preview for images
      mimeType: ev.mimeType,
      size: ev.size,
    }));
  }, [incident?.evidence]);

  // Create unified activity feed from timeline + evidence
  const activityFeed = useMemo(() => {
    const activities = [];

    // Add timeline events
    if (incident?.timeline) {
      incident.timeline.forEach((event) => {
        activities.push({
          id: `timeline-${event.at}-${event.event}`,
          type: event.event,
          at: new Date(event.at),
          actorId: event.actorId,
          data: event.data,
          source: "timeline",
        });
      });
    }

    // Add evidence as activities
    if (incident?.evidence) {
      incident.evidence.forEach((ev) => {
        activities.push({
          id: ev.id,
          type: "EVIDENCE",
          at: new Date(ev.uploadedAt),
          actorId: ev.uploadedBy,
          data: {
            filename: ev.filename,
            description: ev.description,
            downloadUrl: ev.downloadUrl,
            mimeType: ev.mimeType,
            size: ev.size,
            evidenceId: ev.id,
          },
          source: "evidence",
        });
      });
    }

    // Sort by date
    activities.sort((a, b) => {
      return activityOrder === "desc" ? b.at - a.at : a.at - b.at;
    });

    // Filter by type
    if (activityFilter !== "all") {
      const filterMap = {
        evidence: "EVIDENCE",
        status: "STATUS_CHANGED",
        responsibility: "RESPONSIBILITY_ATTRIBUTED",
        system: "CREATED",
      };
      return activities.filter((a) => a.type === filterMap[activityFilter]);
    }

    return activities;
  }, [incident?.timeline, incident?.evidence, activityOrder, activityFilter]);

  // Group activities into bundles (similar events within 5 minutes)
  const bundledActivities = useMemo(() => {
    const bundles = [];
    const BUNDLE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

    for (let i = 0; i < activityFeed.length; i++) {
      const current = activityFeed[i];
      const bundle = {
        id: `bundle-${i}`,
        type: current.type,
        at: current.at,
        actorId: current.actorId,
        items: [current],
        isBundle: false,
      };

      // Try to bundle with following items
      for (let j = i + 1; j < activityFeed.length; j++) {
        const next = activityFeed[j];
        const timeDiff = Math.abs(next.at - current.at);

        if (
          next.type === current.type &&
          next.actorId === current.actorId &&
          timeDiff <= BUNDLE_WINDOW_MS
        ) {
          bundle.items.push(next);
          bundle.isBundle = true;
          i = j; // Skip bundled items
        } else {
          break;
        }
      }

      bundles.push(bundle);
    }

    return bundles;
  }, [activityFeed]);

  const handleStatusUpdate = async (status, reason) => {
    try {
      const result = await updateIncidentStatus(
        orderId,
        incidentId,
        status,
        {
          actorId: "current-user", // TODO: Get from auth context
          reason,
        }
      );
      if (result.success) {
        setNotification({
          open: true,
          message: "Status updated successfully",
          severity: "success",
        });
        setStatusDialogOpen(false);
        fetchIncident();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to update status",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleResponsibilityUpdate = async (responsibility, reason) => {
    try {
      const result = await attributeResponsibility(
        orderId,
        incidentId,
        responsibility,
        "current-user", // TODO: Get from auth context
        reason
      );
      if (result.success) {
        setNotification({
          open: true,
          message: "Responsibility updated successfully",
          severity: "success",
        });
        setResponsibilityDialogOpen(false);
        fetchIncident();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to update responsibility",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to update responsibility",
        severity: "error",
      });
    }
  };

  const handleEvidenceUpload = async (file, description) => {
    try {
      const result = await addIncidentEvidence(orderId, incidentId, file, description);
      if (result.success) {
        setNotification({
          open: true,
          message: "Evidence uploaded successfully",
          severity: "success",
        });
        setEvidenceDialogOpen(false);
        fetchIncident();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to upload evidence",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to upload evidence",
        severity: "error",
      });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !incident) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Incident not found"}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/orders/${orderId}/incidents`)}
        >
          Back to Incidents
        </Button>
        <Typography variant="h4" component="h1">
          Incident {incident.id}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Chip
                    label={incident.severity}
                    color={
                      incident.severity === "CRITICAL"
                        ? "error"
                        : incident.severity === "HIGH"
                        ? "warning"
                        : "info"
                    }
                  />
                  <Chip label={incident.status} />
                  <Chip label={incident.type?.replace(/_/g, " ")} />
                </Box>

                <Typography variant="h6" gutterBottom>
                  {incident.type?.replace(/_/g, " ")}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {incident.description}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Schedule fontSize="small" />
                      <Typography variant="body2">
                        Occurred: {format(new Date(incident.occurredAt), "PPpp")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Schedule fontSize="small" />
                      <Typography variant="body2">
                        Reported: {format(new Date(incident.reportedAt), "PPpp")}
                      </Typography>
                    </Box>
                  </Grid>
                  {incident.reportedBy && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Person fontSize="small" />
                        <Typography variant="body2">
                          Reported by: {incident.reportedBy.actorType} ({incident.reportedBy.actorId})
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {incident.currentResponsibility && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Warning fontSize="small" />
                        <Typography variant="body2">
                          Responsibility: {incident.currentResponsibility}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Unified Activity Feed */}
              {(bundledActivities.length > 0 || evidenceItems.length > 0) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <ActivityFeed
                    bundles={bundledActivities}
                    evidenceItems={evidenceItems}
                    order={activityOrder}
                    filter={activityFilter}
                    onOrderChange={setActivityOrder}
                    onFilterChange={setActivityFilter}
                    expandedBundles={expandedBundles}
                    onToggleBundle={(bundleId) => {
                      const newExpanded = new Set(expandedBundles);
                      if (newExpanded.has(bundleId)) {
                        newExpanded.delete(bundleId);
                      } else {
                        newExpanded.add(bundleId);
                      }
                      setExpandedBundles(newExpanded);
                    }}
                    onPreviewEvidence={(item) => {
                      setPreviewItem(item);
                      setPreviewOpen(true);
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={() => setEvidenceDialogOpen(true)}
                >
                  Upload Evidence
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setStatusDialogOpen(true)}
                >
                  Update Status
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setResponsibilityDialogOpen(true)}
                >
                  Attribute Responsibility
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <StatusUpdateDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        currentStatus={incident.status}
        onUpdate={handleStatusUpdate}
      />

      {/* Responsibility Update Dialog */}
      <ResponsibilityUpdateDialog
        open={responsibilityDialogOpen}
        onClose={() => setResponsibilityDialogOpen(false)}
        currentResponsibility={incident.currentResponsibility}
        onUpdate={handleResponsibilityUpdate}
      />

      {/* Evidence Upload Dialog */}
      <EvidenceUploadDialog
        open={evidenceDialogOpen}
        onClose={() => setEvidenceDialogOpen(false)}
        onUpload={handleEvidenceUpload}
      />

      {/* Evidence Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewItem?.description || previewItem?.filename || "Evidence Preview"}
        </DialogTitle>
        <DialogContent>
          {previewItem && (
            <Box sx={{ textAlign: "center" }}>
              {previewItem.type === "image" && (
                <img
                  src={previewItem.downloadUrl || previewItem.previewUrl}
                  alt={previewItem.description || "Evidence image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              )}
              {previewItem.type === "document" && previewItem.mimeType === "application/pdf" && (
                <Box sx={{ width: "100%", height: "70vh" }}>
                  <iframe
                    src={previewItem.downloadUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                    }}
                    title="PDF Preview"
                  />
                </Box>
              )}
              {previewItem.type === "document" &&
                previewItem.mimeType !== "application/pdf" && (
                  <Box sx={{ p: 4 }}>
                    <AttachFile
                      sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {previewItem.filename || "Document"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This document cannot be previewed in the browser.
                    </Typography>
                  </Box>
                )}
              {previewItem.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
                  {previewItem.description}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {previewItem && (
            <Button
              startIcon={<AttachFile />}
              href={previewItem.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
            >
              Open Original
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Status Update Dialog Component
const StatusUpdateDialog = ({ open, onClose, currentStatus, onUpdate }) => {
  const [status, setStatus] = useState(currentStatus);
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    onUpdate(status, reason);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Status</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            fullWidth
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Responsibility Update Dialog Component
const ResponsibilityUpdateDialog = ({
  open,
  onClose,
  currentResponsibility,
  onUpdate,
}) => {
  const [responsibility, setResponsibility] = useState(currentResponsibility || "UNDETERMINED");
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    onUpdate(responsibility, reason);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attribute Responsibility</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Responsibility"
            value={responsibility}
            onChange={(e) => setResponsibility(e.target.value)}
            fullWidth
          >
            {RESPONSIBILITY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Evidence Upload Dialog Component
const EvidenceUploadDialog = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");

  const handleSubmit = () => {
    if (file) {
      onUpload(file, description);
      setFile(null);
      setDescription("");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Evidence</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Button variant="outlined" component="label">
            Select File
            <input
              type="file"
              hidden
              onChange={(e) => setFile(e.target.files[0])}
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary">
              Selected: {file.name}
            </Typography>
          )}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!file}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Timeline Event Component
const TimelineEvent = ({ event, isLast = false, compact = false }) => {
  const getEventIcon = () => {
    const eventType = event.event || event.type;
    switch (eventType) {
      case "CREATED":
        return <AddCircle color="primary" fontSize="small" />;
      case "EVIDENCE":
      case "EVIDENCE_ADDED":
        return <AttachFile color="success" fontSize="small" />;
      case "STATUS_CHANGED":
        return <ChangeCircle color="info" fontSize="small" />;
      case "RESPONSIBILITY_ATTRIBUTED":
        return <Assignment color="warning" fontSize="small" />;
      case "RESOLVED":
        return <CheckCircle color="success" fontSize="small" />;
      case "CLOSED":
        return <Close color="action" fontSize="small" />;
      default:
        return <Timeline color="action" fontSize="small" />;
    }
  };

  const getEventLabel = () => {
    const eventType = event.event || event.type;
    return eventType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatEventData = () => {
    if (!event.data || Object.keys(event.data).length === 0) return null;

    const data = event.data;

    // Format EVIDENCE_ADDED or EVIDENCE events
    const eventType = event.event || event.type;
    if (eventType === "EVIDENCE_ADDED" || eventType === "EVIDENCE") {
      // Handle both evidenceId and evidenceld (typo) for backward compatibility
      const evidenceId = data.evidenceId || data.evidenceld;
      return (
        <Box
          sx={{
            mt: 0.5,
            pl: 2,
            backgroundColor: "action.hover",
            borderRadius: 1,
            p: 1,
          }}
        >
          {evidenceId && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Evidence ID: <strong>{evidenceId}</strong>
            </Typography>
          )}
          {data.filename && (
            <Typography variant="caption" color="text.secondary" display="block">
              File: <strong>{data.filename}</strong>
            </Typography>
          )}
        </Box>
      );
    }

    // Format STATUS_CHANGED events
    if (event.event === "STATUS_CHANGED") {
      return (
        <Box
          sx={{
            mt: 0.5,
            pl: 2,
            backgroundColor: "action.hover",
            borderRadius: 1,
            p: 1,
          }}
        >
          {data.fromStatus && data.toStatus && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              <Chip
                label={data.fromStatus}
                size="small"
                sx={{ mr: 0.5, height: 20, fontSize: "0.7rem" }}
              />
              →
              <Chip
                label={data.toStatus}
                size="small"
                color="primary"
                sx={{ ml: 0.5, height: 20, fontSize: "0.7rem" }}
              />
            </Typography>
          )}
          {data.reason && (
            <Typography variant="caption" color="text.secondary" display="block">
              Reason: {data.reason}
            </Typography>
          )}
        </Box>
      );
    }

    // Format RESPONSIBILITY_ATTRIBUTED events
    if (event.event === "RESPONSIBILITY_ATTRIBUTED") {
      return (
        <Box
          sx={{
            mt: 0.5,
            pl: 2,
            backgroundColor: "action.hover",
            borderRadius: 1,
            p: 1,
          }}
        >
          {data.responsibility && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Responsibility:{" "}
              <Chip
                label={data.responsibility}
                size="small"
                color="warning"
                sx={{ ml: 0.5, height: 20, fontSize: "0.7rem" }}
              />
            </Typography>
          )}
          {data.rationale && (
            <Typography variant="caption" color="text.secondary" display="block">
              Rationale: {data.rationale}
            </Typography>
          )}
        </Box>
      );
    }

    // Fallback: show formatted key-value pairs for other events
    return (
      <Box
        sx={{
          mt: 0.5,
          pl: 2,
          backgroundColor: "action.hover",
          borderRadius: 1,
          p: 1,
        }}
      >
        {Object.entries(data).map(([key, value]) => (
          <Typography key={key} variant="caption" color="text.secondary" display="block">
            {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}:{" "}
            <strong>{String(value)}</strong>
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        mb: isLast ? 0 : 2,
        pb: isLast ? 0 : 2,
        position: "relative",
        "&:not(:last-child)::after": {
          content: '""',
          position: "absolute",
          left: "11px",
          top: "32px",
          bottom: "-16px",
          width: "2px",
          backgroundColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        {getEventIcon()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Typography variant="body2" fontWeight="medium" sx={{ flexShrink: 0 }}>
            {getEventLabel()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {format(new Date(event.at), "PPpp")}
          </Typography>
        </Box>
        {event.actorId && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            By: <strong>{event.actorId}</strong>
          </Typography>
        )}
        {formatEventData()}
      </Box>
    </Box>
  );
};

// Activity Feed Component - Unified timeline + evidence
const ActivityFeed = ({
  bundles,
  evidenceItems,
  order,
  filter,
  onOrderChange,
  onFilterChange,
  expandedBundles,
  onToggleBundle,
  onPreviewEvidence,
}) => {
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "evidence", label: "Evidence" },
    { value: "status", label: "Status" },
    { value: "responsibility", label: "Responsibility" },
    { value: "system", label: "System" },
  ];

  return (
    <Box>
      {/* Header with filters and sort */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Activity
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* Filter chips */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {filterOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                onClick={() => onFilterChange(option.value)}
                color={filter === option.value ? "primary" : "default"}
                variant={filter === option.value ? "filled" : "outlined"}
                sx={{ height: 24, fontSize: "0.7rem" }}
              />
            ))}
          </Box>
          {/* Sort button */}
          <Button
            size="small"
            startIcon={order === "desc" ? <ArrowDownward /> : <ArrowUpward />}
            onClick={() => onOrderChange(order === "desc" ? "asc" : "desc")}
            sx={{ minWidth: "auto", height: 28 }}
          >
            {order === "desc" ? "Newest First" : "Oldest First"}
          </Button>
        </Box>
      </Box>

      {/* Activity items */}
      <Box>
        {bundles.map((bundle, idx) => (
          <ActivityBundle
            key={bundle.id}
            bundle={bundle}
            evidenceItems={evidenceItems}
            isLast={idx === bundles.length - 1}
            isExpanded={expandedBundles.has(bundle.id)}
            onToggle={() => onToggleBundle(bundle.id)}
            onPreviewEvidence={onPreviewEvidence}
          />
        ))}
      </Box>
    </Box>
  );
};

// Activity Bundle Component - Handles single events and bundles
const ActivityBundle = ({
  bundle,
  evidenceItems,
  isLast,
  isExpanded,
  onToggle,
  onPreviewEvidence,
}) => {
  const getEventIcon = () => {
    switch (bundle.type) {
      case "CREATED":
        return <AddCircle color="primary" fontSize="small" />;
      case "EVIDENCE":
      case "EVIDENCE_ADDED":
        return <AttachFile color="success" fontSize="small" />;
      case "STATUS_CHANGED":
        return <ChangeCircle color="info" fontSize="small" />;
      case "RESPONSIBILITY_ATTRIBUTED":
        return <Assignment color="warning" fontSize="small" />;
      case "RESOLVED":
        return <CheckCircle color="success" fontSize="small" />;
      case "CLOSED":
        return <Close color="action" fontSize="small" />;
      default:
        return <Timeline color="action" fontSize="small" />;
    }
  };

  const getEventLabel = () => {
    if (bundle.type === "EVIDENCE") {
      return bundle.isBundle
        ? `Evidence Added (${bundle.items.length})`
        : "Evidence Added";
    }
    return bundle.type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleEvidenceClick = (evidenceData) => {
    const evidenceItem = evidenceItems.find((item) => item.id === evidenceData.evidenceId);
    if (evidenceItem) {
      onPreviewEvidence(evidenceItem);
    } else {
      // Fallback: open in new tab
      window.open(evidenceData.downloadUrl, "_blank");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        mb: isLast ? 0 : 2,
        pb: isLast ? 0 : 2,
        position: "relative",
        "&:not(:last-child)::after": {
          content: '""',
          position: "absolute",
          left: "11px",
          top: bundle.isBundle && isExpanded ? "80px" : "32px",
          bottom: "-16px",
          width: "2px",
          backgroundColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        {getEventIcon()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Bundle header */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Typography variant="body2" fontWeight="medium" sx={{ flexShrink: 0 }}>
            {getEventLabel()}
          </Typography>
          {bundle.isBundle && (
            <Chip
              label={bundle.items.length}
              size="small"
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {format(bundle.at, "PPpp")}
          </Typography>
          {bundle.isBundle && (
            <Button
              size="small"
              onClick={onToggle}
              sx={{ minWidth: "auto", p: 0.5, ml: "auto" }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          By: <strong>{bundle.actorId}</strong>
        </Typography>

        {/* Bundle content */}
        {bundle.isBundle && !isExpanded ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Click to expand {bundle.items.length} items
          </Typography>
        ) : (
          <Box>
            {(isExpanded || !bundle.isBundle ? bundle.items : [bundle.items[0]]).map(
              (item, itemIdx) => {
                if (item.type === "EVIDENCE" || item.type === "EVIDENCE_ADDED") {
                  return (
                    <EvidenceActivityItem
                      key={item.id || itemIdx}
                      evidenceData={item.data}
                      onPreview={handleEvidenceClick}
                    />
                  );
                }
                return (
                  <TimelineEvent
                    key={item.id || itemIdx}
                    event={item}
                    isLast={itemIdx === bundle.items.length - 1}
                    compact={bundle.isBundle}
                  />
                );
              }
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Evidence Activity Item Component
const EvidenceActivityItem = ({ evidenceData, onPreview }) => {
  return (
    <Box
      sx={{
        mt: 0.5,
        pl: 2,
        backgroundColor: "action.hover",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
        <AttachFile fontSize="small" color="action" sx={{ mt: 0.5 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            component="a"
            href={evidenceData.downloadUrl}
            onClick={(e) => {
              e.preventDefault();
              onPreview(evidenceData);
            }}
            sx={{
              fontWeight: "medium",
              color: "primary.main",
              textDecoration: "none",
              cursor: "pointer",
              display: "block",
              mb: evidenceData.description ? 0.5 : 0,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {evidenceData.filename}
          </Typography>
          {evidenceData.description && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {evidenceData.description}
            </Typography>
          )}
          {evidenceData.evidenceId && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Evidence ID: <strong>{evidenceData.evidenceId}</strong>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default IncidentDetailsView;
